import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as executionApi from '../../services/execution-api.js';
import type { TimelinePayload } from '../../services/execution-api.js';
import { wsClient } from '../../services/websocket-client.js';

type Milestone = {
  key: 'spawned' | 'first-output' | 'last-output' | 'completed';
  label: string;
  time: string | null;
  active: boolean;
  warning?: boolean;
};

@customElement('cp-execution-timeline')
export class CpExecutionTimeline extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) ticketId = '';
  @property({ type: Boolean }) open = false;
  @state() private _timeline: TimelinePayload | null = null;

  private _wsHandler: ((e: Event) => void) | null = null;

  async connectedCallback(): Promise<void> {
    super.connectedCallback();
    await this._fetchTimeline();

    this._wsHandler = (e: Event) => {
      const msg = (e as CustomEvent<{ type: string; payload: Record<string, unknown> }>).detail;
      if (!msg.payload || (msg.payload as { ticketId?: string }).ticketId !== this.ticketId) return;

      if (msg.type === 'execution:warning') {
        if (this._timeline) {
          this._timeline = { ...this._timeline, warningEmittedAt: (msg.payload.warningEmittedAt as string) ?? null };
        }
      } else if (msg.type === 'execution:first-output') {
        if (this._timeline) {
          this._timeline = { ...this._timeline, firstOutputAt: (msg.payload.firstOutputAt as string) ?? null };
        }
      } else if (msg.type === 'execution:timeline-updated') {
        void this._fetchTimeline();
      }
    };
    wsClient.addEventListener('ws-message', this._wsHandler);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._wsHandler) {
      wsClient.removeEventListener('ws-message', this._wsHandler);
      this._wsHandler = null;
    }
  }

  private async _fetchTimeline(): Promise<void> {
    try {
      this._timeline = await executionApi.getExecutionTimeline(this.ticketId);
    } catch {
      this._timeline = null;
    }
  }

  private _toggle(): void {
    this.open = !this.open;
  }

  private _formatTime(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  private _milestones(t: TimelinePayload): Milestone[] {
    const isRunning = t.status === 'running';
    const isDone = !!t.completedAt;
    const hasFailed = t.status === 'failed' || t.status === 'interrupted';

    return [
      {
        key: 'spawned',
        label: 'Spawned',
        time: t.startedAt,
        active: !!t.startedAt,
      },
      {
        key: 'first-output',
        label: 'First Output',
        time: t.firstOutputAt,
        active: !!t.firstOutputAt,
        warning: !!t.warningEmittedAt && !isDone,
      },
      {
        key: 'last-output',
        label: 'Last Output',
        time: t.lastOutputAt,
        active: !!t.lastOutputAt && (isRunning || isDone),
      },
      {
        key: 'completed',
        label: hasFailed ? t.status.charAt(0).toUpperCase() + t.status.slice(1) : 'Completed',
        time: t.completedAt,
        active: isDone,
      },
    ];
  }

  render() {
    const t = this._timeline;
    if (!t) return nothing;

    const milestones = this._milestones(t);

    return html`
      <div class="cp-execution-timeline">
        <button
          class="cp-execution-timeline__toggle"
          @click=${this._toggle}
          aria-expanded=${this.open}
          title="Toggle execution timeline"
        >
          <span class="cp-execution-timeline__toggle-icon">${this.open ? '▾' : '▸'}</span>
          <span class="cp-execution-timeline__toggle-label">Timeline</span>
          ${t.warningEmittedAt && !t.completedAt
            ? html`<span class="cp-execution-timeline__warning-dot" title="Approaching timeout">⚠</span>`
            : nothing}
        </button>

        ${this.open ? html`
          <div class="cp-execution-timeline__body">
            ${milestones.map((m, i) => html`
              <div class="cp-execution-timeline__item ${m.active ? 'cp-execution-timeline__item--active' : ''} ${m.key === 'completed' && t.status === 'failed' ? 'cp-execution-timeline__item--error' : ''}">
                <div class="cp-execution-timeline__connector ${i === 0 ? 'cp-execution-timeline__connector--first' : ''}"></div>
                <div class="cp-execution-timeline__dot"></div>
                <div class="cp-execution-timeline__content">
                  <span class="cp-execution-timeline__label">
                    ${m.label}
                    ${m.warning ? html`<span class="cp-execution-timeline__warning-inline" title="Approaching timeout">⚠</span>` : nothing}
                  </span>
                  <span class="cp-execution-timeline__time">${this._formatTime(m.time)}</span>
                </div>
              </div>
            `)}
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-execution-timeline': CpExecutionTimeline;
  }
}
